/**
 * Attributes admin — `/catalog/attributes` (SPEC screen 3). Master-detail:
 * cursor DataTable + edit panel. Code is immutable after create; usage
 * refusals arrive as typed 422 problems (`attribute_in_use` with
 * attribute_id/usage_count, `attribute_option_in_use` adding option) and
 * render inline. Every PUT requires the If-Match captured by the detail GET —
 * if that read fails, the edit is blocked rather than writing blind.
 */
import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@erp/ui";
import { z } from "zod";

import { DataTable, badgeCell } from "@/components/data-table";
import {
  ConflictDialog,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  surfaceProblem,
  useFormSubmit,
  useZodForm,
} from "@/components/form";
import {
  claimIdempotencyAttempt,
  isAmbiguousIdempotencyFailure,
  isIdempotencyKeyReuse,
  pageFetcher,
  useCursorList,
  useEtagRef,
  type IdempotencyAttempt,
} from "@/features/shared/ops-api";
import { ApiError } from "@/lib/api/errors";

import {
  ATTRIBUTE_TYPES,
  createAttribute,
  deleteAttribute,
  getAttribute,
  updateAttribute,
  type Attribute,
  type AttributeInput,
  type AttributeType,
} from "../api";
import { catalogKeys } from "../queries";

const attributeSchema = z.object({
  code: z
    .string()
    .min(1)
    .refine((value) => /^[a-z][a-z0-9_]*$/.test(value), {
      params: { i18nKey: "catalog.attributes.invalidCode" },
      message: "Lowercase slug (a-z, 0-9, _)",
    }),
  type: z.enum(ATTRIBUTE_TYPES),
  options: z.array(z.string().min(1)),
});

type AttributeFormValues = z.infer<typeof attributeSchema>;

const EMPTY: AttributeFormValues = { code: "", type: "text", options: [] };

export interface AttributesPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}

export function AttributesPage({
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}: AttributesPageProps = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /** null = create mode; otherwise the attribute being edited. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [optionDraft, setOptionDraft] = useState("");
  /** Inline banner for the typed 422 usage refusals. */
  const [usageError, setUsageError] = useState<string | null>(null);
  const etagRef = useEtagRef();

  const query = useCursorList(catalogKeys.attributes.list(), pageFetcher<Attribute>("/attributes"));

  const form = useZodForm(attributeSchema, { defaultValues: EMPTY });
  const type = form.watch("type");
  const options = form.watch("options");
  const canOpenEditor = canUpdate || canDelete;
  const canEditCurrent = editingId === null ? canCreate : canUpdate;

  const startCreate = () => {
    setEditingId(null);
    setUsageError(null);
    etagRef.current = null;
    form.reset(EMPTY);
  };

  const startEdit = async (attribute: Attribute) => {
    setUsageError(null);
    try {
      const { data, etag } = await getAttribute(attribute.id);
      etagRef.current = etag;
      setEditingId(attribute.id);
      form.reset({ code: data.code, type: data.type, options: [...data.options] });
    } catch {
      // Every PUT requires the captured If-Match — block the edit rather
      // than degrading to a blind write.
      toast({
        variant: "destructive",
        title: t(
          "catalog.attributes.loadDetailFailed",
          "The attribute could not be loaded for editing — try again.",
        ),
      });
    }
  };

  /** Render the typed usage problems inline; everything else falls through. */
  const surfaceUsageProblem = (error: unknown): boolean => {
    if (!(error instanceof ApiError)) return false;
    const problem = error.problem as
      { usage_count?: number; option?: string; detail?: string } | undefined;
    if (error.code === "attribute_in_use") {
      setUsageError(
        t(
          "catalog.attributes.inUse",
          "This attribute is referenced by {{count}} catalog records and cannot be changed or deleted.",
          { count: problem?.usage_count ?? 0 },
        ),
      );
      return true;
    }
    if (error.code === "attribute_option_in_use") {
      setUsageError(
        t(
          "catalog.attributes.optionInUse",
          "Option {{option}} is still used by {{count}} variants and cannot be removed.",
          { option: problem?.option ?? "", count: problem?.usage_count ?? 0 },
        ),
      );
      return true;
    }
    return false;
  };

  // One Idempotency-Key per logical create, replayed across user retries.
  const createAttemptRef = useRef<IdempotencyAttempt | null>(null);

  const { onSubmit, isPending, conflict, dismissConflict } = useFormSubmit(
    form,
    async (values: AttributeFormValues) => {
      const payload: AttributeInput = {
        code: values.code,
        type: values.type,
        // The contract's options member is always an array — [] for text/number.
        options: values.type === "select" ? values.options : [],
      };
      if (editingId === null) {
        const attempt = await claimIdempotencyAttempt(createAttemptRef, payload);
        try {
          const created = await createAttribute(payload, attempt.key);
          createAttemptRef.current = null;
          return created;
        } catch (error) {
          if (!isAmbiguousIdempotencyFailure(error) && !isIdempotencyKeyReuse(error)) {
            createAttemptRef.current = null;
          }
          throw error;
        }
      }
      const ifMatch = etagRef.current;
      if (ifMatch === null) {
        // Edit mode is only entered after a successful ETag-carrying read.
        throw new ApiError("Missing If-Match ETag; reload the attribute first", 428);
      }
      const { data, etag } = await updateAttribute(editingId, payload, ifMatch);
      etagRef.current = etag;
      return data;
    },
    {
      onSuccess: async (saved) => {
        createAttemptRef.current = null;
        setUsageError(null);
        toast({ variant: "success", title: t("catalog.attributes.saved", "Attribute saved") });
        await queryClient.invalidateQueries({ queryKey: catalogKeys.attributes.all });
        setEditingId(saved.id);
      },
      fieldMap: { options: "options" },
      onError: (error) => {
        if (surfaceUsageProblem(error)) return;
        surfaceProblem(error, t);
      },
    },
  );

  const remove = async (attribute: Attribute) => {
    try {
      await deleteAttribute(attribute.id);
      await queryClient.invalidateQueries({ queryKey: catalogKeys.attributes.all });
      if (editingId === attribute.id) startCreate();
    } catch (error) {
      if (surfaceUsageProblem(error)) return;
      toast({
        variant: "destructive",
        title: t("catalog.attributes.deleteFailed", "Attribute could not be deleted"),
      });
    }
  };

  const typeLabel = (value: string): string => t(`catalog.attributes.type.${value}`, value);

  const columns = useMemo<ColumnDef<Attribute>[]>(
    () => [
      {
        accessorKey: "code",
        id: "code",
        header: t("catalog.attributes.columns.code", "Code"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "name",
        id: "name",
        header: t("catalog.attributes.columns.name", "Name"),
        enableSorting: false,
        cell: (ctx) => ctx.getValue<string>(),
      },
      {
        accessorKey: "type",
        id: "type",
        header: t("catalog.attributes.columns.type", "Type"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            { text: "outline", number: "secondary", select: "default" },
            typeLabel(value),
          );
        },
      },
      {
        accessorKey: "options",
        id: "options",
        header: t("catalog.attributes.columns.options", "Options"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string[]>();
          return value.length === 0 ? (
            <span className="text-muted-foreground">&mdash;</span>
          ) : (
            <span className="flex flex-wrap gap-1">
              {value.map((option) => (
                <Badge key={option} variant="outline">
                  {option}
                </Badge>
              ))}
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- label helper derives from t
    [t],
  );

  const addOption = () => {
    const trimmed = optionDraft.trim();
    if (trimmed === "" || options.includes(trimmed)) return;
    form.setValue("options", [...options, trimmed], { shouldDirty: true });
    setOptionDraft("");
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t("catalog.attributes.title", "Attributes")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("catalog.attributes.subtitle", "Definitions that drive variant attribute values.")}
          </p>
        </div>
        {canCreate ? (
          <Button onClick={startCreate}>{t("catalog.attributes.new", "New attribute")}</Button>
        ) : null}
      </div>

      <div
        className={
          canCreate || canOpenEditor
            ? "grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]"
            : "grid grid-cols-1 gap-4"
        }
      >
        <DataTable
          columns={columns}
          query={query}
          getRowId={(row) => row.id}
          onRowClick={canOpenEditor ? (row) => void startEdit(row) : undefined}
          showColumnsMenu={false}
          emptyMessage={t(
            "catalog.attributes.empty",
            "No attributes defined — create the first one.",
          )}
          errorMessage={t("catalog.attributes.loadError", "Attributes could not be loaded.")}
        />

        {canCreate || canOpenEditor ? (
          <Card>
            <CardHeader>
              <CardTitle>
                <h2>
                  {editingId === null
                    ? t("catalog.attributes.createTitle", "Create attribute")
                    : t("catalog.attributes.editTitle", "Edit attribute")}
                </h2>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t("catalog.attributes.fields.code", "Code")}</FormLabel>
                      <FormControl>
                        <Input
                          dir="ltr"
                          className="font-mono"
                          disabled={editingId !== null || !canEditCurrent}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t("catalog.attributes.fields.type", "Type")}</FormLabel>
                      {/* key: remount when reset() swaps the value while closed. */}
                      <Select
                        key={field.value}
                        value={field.value}
                        onValueChange={(next) => field.onChange(next as AttributeType)}
                        disabled={!canEditCurrent}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ATTRIBUTE_TYPES.map((candidate) => (
                            <SelectItem key={candidate} value={candidate}>
                              {typeLabel(candidate)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {type === "select" ? (
                  <FormField
                    control={form.control}
                    name="options"
                    render={() => (
                      <FormItem>
                        <FormLabel>{t("catalog.attributes.fields.options", "Options")}</FormLabel>
                        <div className="flex flex-wrap gap-1">
                          {options.map((option) => (
                            <Badge key={option} variant="secondary" className="gap-1">
                              {option}
                              {canEditCurrent ? (
                                <button
                                  type="button"
                                  aria-label={t(
                                    "catalog.attributes.removeOption",
                                    "Remove option {{option}}",
                                    { option },
                                  )}
                                  onClick={() =>
                                    form.setValue(
                                      "options",
                                      options.filter((candidate) => candidate !== option),
                                      { shouldDirty: true },
                                    )
                                  }
                                  className="rounded-sm hover:opacity-70"
                                >
                                  <X className="size-3" aria-hidden="true" />
                                </button>
                              ) : null}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <Input
                              disabled={!canEditCurrent}
                              value={optionDraft}
                              onChange={(event) => setOptionDraft(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  addOption();
                                }
                              }}
                              placeholder={t(
                                "catalog.attributes.addOptionPlaceholder",
                                "Add option…",
                              )}
                            />
                          </FormControl>
                          {canEditCurrent ? (
                            <Button type="button" variant="outline" size="sm" onClick={addOption}>
                              {t("catalog.attributes.addOption", "Add")}
                            </Button>
                          ) : null}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null}

                {usageError !== null ? (
                  <p
                    role="alert"
                    className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-sm text-destructive"
                  >
                    {usageError}
                  </p>
                ) : null}

                <div className="flex items-center justify-between gap-2">
                  {canEditCurrent ? (
                    <Button type="submit" loading={isPending}>
                      {t("catalog.attributes.save", "Save attribute")}
                    </Button>
                  ) : (
                    <span />
                  )}
                  {editingId !== null && canDelete ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const current = query.rows.find((row) => row.id === editingId);
                        if (current !== undefined) void remove(current);
                      }}
                    >
                      {t("catalog.attributes.delete", "Delete")}
                    </Button>
                  ) : null}
                </div>
              </Form>
            </CardContent>
          </Card>
        ) : null}
      </div>

      <ConflictDialog
        open={conflict}
        onReload={() => {
          dismissConflict();
          const current = query.rows.find((row) => row.id === editingId);
          if (current !== undefined) void startEdit(current);
        }}
        onDismiss={dismissConflict}
      />
    </div>
  );
}
